import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Alert } from "@/ui/alert";
import { AlertCircle } from "lucide-react";
import { apiRequest } from "../../../../../Middleware/API/apiRequest";

interface RegisterProps {
  setIsLoader: (v: boolean) => void;
}

const schema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .regex(/^\S+@\S+\.\S+$/, "Please enter a valid email."),
  fname: z.string().min(1, "First name is required"),
  lname: z.string().min(1, "Last name is required"),
});

type FormValues = z.infer<typeof schema>;

/**
 * Register form (react-hook-form + zod, shadcn UI; behavior unchanged)
 * - Keeps same API payload shape and navigation behavior
 */
const Register: React.FC<RegisterProps> = ({ setIsLoader }) => {
  const navigate = useNavigate();

  const [alert, setAlert] = React.useState<{
    type: "danger";
    message: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const onSubmit = async ({ email, fname, lname }: FormValues) => {
    setAlert(null);
    sessionStorage.setItem("isRegisteredMessageShown", "false");
    setIsLoader(true);

    const payload = {
      Appuservm: {
        UserRoles: [],
        UserProfile: {
          UserName: email,
          AccessCode: "00000000-0000-0000-0000-000000000000",
          FirstName: fname,
          MiddleName: "",
          LastName: lname,
          TimeZone: "",
          Email: email,
          FileData: "",
          AuthGatewayId: "OISDB",
        },
      },
    };

    const baseURL = `${process.env.BASE_URL}/api/Register/User`;
    const request: any = {
      baseURL,
      method: "POST",
      headers: {
        "content-type": "application/json",
        Accept: "*/*",
        "X-Application-Id": process.env.APP_ID,
        "X-App-Key": process.env.APP_KEY,
        "X-App-Secret": process.env.APP_SECRET,
      },
      body: JSON.stringify(payload),
    };

    try {
      const response: any = await apiRequest(request, false);
      if (!response?.IsSuccess) {
        setAlert({
          type: "danger",
          message: response?.StatusDesc ?? "Registration failed.",
        });
      } else {
        navigate(`/login`, {
          state: {
            isRegistered: true,
          },
        });
      }
    } catch {
      setAlert({
        type: "danger",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {alert && (
        <Alert variant="destructive" role="alert" className="p-2">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="truncate" title={alert.message}>
              {alert.message}
            </span>
          </div>
        </Alert>
      )}

      {/* Email */}
      <div className="space-y-1.5">
        <Label htmlFor="email" className="font-medium text-foreground">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          aria-invalid={errors.email ? "true" : "false"}
          aria-describedby={errors.email ? "email-error" : undefined}
          {...register("email")}
        />
        {errors.email && (
          <p id="email-error" className="text-sm text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>

      {/* First Name */}
      <div className="space-y-1.5">
        <Label htmlFor="fname" className="font-medium text-foreground">
          First Name
        </Label>
        <Input
          id="fname"
          type="text"
          aria-invalid={errors.fname ? "true" : "false"}
          aria-describedby={errors.fname ? "fname-error" : undefined}
          {...register("fname")}
        />
        {errors.fname && (
          <p id="fname-error" className="text-sm text-destructive">
            {errors.fname.message}
          </p>
        )}
      </div>

      {/* Last Name */}
      <div className="space-y-1.5">
        <Label htmlFor="lname" className="font-medium text-foreground">
          Last Name
        </Label>
        <Input
          id="lname"
          type="text"
          aria-invalid={errors.lname ? "true" : "false"}
          aria-describedby={errors.lname ? "lname-error" : undefined}
          {...register("lname")}
        />
        {errors.lname && (
          <p id="lname-error" className="text-sm text-destructive">
            {errors.lname.message}
          </p>
        )}
      </div>

      <div className="mt-6">
        <Button
          type="submit"
          fullWidth
          size="lg"
          variant="primary"
          disabled={isSubmitting}
        >
          Register
        </Button>
      </div>
    </form>
  );
};

export default Register;
